/**
 * Which deployment an event came from.
 *
 * Highlight runs one PostHog project for every environment, deliberately: one
 * person, one funnel, no cross-project joins to maintain. The cost of that
 * choice is that a dev machine and CI write into the same place production
 * does, so the library has to be the thing that refuses.
 */
export type AnalyticsEnvironment = 'development' | 'test' | 'staging' | 'experimental' | 'production'

/** Environments whose events would be noise in a funnel read for real users. */
const NEVER_SENDS = new Set<AnalyticsEnvironment>(['development', 'test'])

export interface EnvironmentOptions {
  /**
   * Where this is running. Unrecognized values are treated as production,
   * because the failure that matters is silently dropping real events, not
   * sending a few extra from an environment nobody named.
   */
  environment?: string
  /**
   * Send from a non-sending environment anyway, to exercise the pipeline.
   * Never default this to true and never read it from a shared config.
   */
  forceSend?: boolean
  /**
   * A loopback URL to send to instead of PostHog, e.g. `http://localhost:9999`
   * running `pnpm --filter @highlight/analytics sink`.
   *
   * Safe to read from a `.dev.vars` or `.env.local`, unlike `forceSend`: it is
   * honoured only when it is a loopback address and only from an environment
   * that does not send, so it can neither route dev events into the real
   * project nor point a deployed Worker at a laptop.
   */
  localSink?: string
}

const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]'])

/**
 * The sink to send to instead of PostHog, when one applies.
 *
 * Anything that is not a loopback URL, or that arrives from an environment
 * that sends, is refused with a warning rather than dropped silently. A
 * misspelt host would otherwise read as "no events", which is also what a
 * working environment gate looks like.
 */
export function localSink({ environment, localSink }: EnvironmentOptions): string | undefined {
  if (!localSink) {
    return undefined
  }
  if (!NEVER_SENDS.has(normalizeEnvironment(environment))) {
    console.warn(`[analytics] ignoring localSink "${localSink}": only development and test may use one`)
    return undefined
  }
  let url: URL
  try {
    url = new URL(localSink)
  } catch {
    console.warn(`[analytics] ignoring localSink "${localSink}": not a URL`)
    return undefined
  }
  if ((url.protocol !== 'http:' && url.protocol !== 'https:') || !LOOPBACK_HOSTS.has(url.hostname)) {
    console.warn(`[analytics] ignoring localSink "${localSink}": not a loopback http(s) URL`)
    return undefined
  }
  return url.origin
}

export function normalizeEnvironment(environment: string | undefined): AnalyticsEnvironment {
  switch (environment) {
    case 'development':
    case 'dev':
      return 'development'
    case 'test':
    case 'ci':
      return 'test'
    case 'staging':
    case 'alpha':
      return 'staging'
    case 'experimental':
    case 'beta':
      return 'experimental'
    default:
      return 'production'
  }
}

/**
 * Whether events from this environment should reach PostHog.
 *
 * `test` is the load-bearing case: the project key is compiled into the
 * library, so without this an unmocked `capture()` in any unit test posts a
 * real event into the project live experiments are read from, and ingested
 * events cannot be selectively deleted.
 */
/**
 * Whether a surface may send at all, environment and test runner together.
 *
 * The one place that decides it. `shouldSend` alone is not the whole rule: a
 * caller that forgets to declare `test` would otherwise post real events into
 * the production project from a test run.
 */
export function sendsEvents(options: EnvironmentOptions): boolean {
  // A sink is a fake, so the test-runner guard has nothing to protect.
  if (localSink(options)) {
    return true
  }
  return shouldSend(options) && !(isTestRunner() && !options.forceSend)
}

/**
 * Whether flags may be evaluated against PostHog: everywhere but tests.
 *
 * Development evaluates so a flag can be tested on and off locally. A read
 * writes nothing to PostHog, and exposures still go through `sendsEvents`,
 * so a dev machine never records one. Tests read fallbacks so they stay
 * offline and deterministic.
 */
export function evaluatesFlags(options: EnvironmentOptions): boolean {
  if (options.forceSend) {
    return true
  }
  return normalizeEnvironment(options.environment) !== 'test' && !isTestRunner()
}

export function shouldSend({ environment, forceSend }: EnvironmentOptions): boolean {
  if (forceSend) {
    return true
  }
  return !NEVER_SENDS.has(normalizeEnvironment(environment))
}

/**
 * True when a test runner is driving, whatever the caller declared.
 *
 * A belt to `shouldSend`'s braces: a consumer that forgets to pass
 * `environment` in its test setup would otherwise fall through to production.
 */
export function isTestRunner(): boolean {
  const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process
  return Boolean(proc?.env?.VITEST || proc?.env?.JEST_WORKER_ID || proc?.env?.NODE_ENV === 'test')
}
